import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Clock3, RefreshCcw, Users } from "lucide-react";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { getUserStats } from "@/lib/api";

const formatDateTime = (value) => {
  if (!value) return "Henüz yok";
  return new Intl.DateTimeFormat("tr-TR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
};

const percent = (correct, answered) => {
  if (!answered) return 0;
  return Math.round((correct / answered) * 100);
};

const StatBox = ({ label, value, tone = "neutral" }) => {
  const tones = {
    neutral: "bg-[#f5f6f2] text-[#1c2723]",
    good: "bg-[#4a6741]/10 text-[#4a6741]",
    bad: "bg-[#a55e3f]/10 text-[#a55e3f]",
    focus: "bg-[#2e4733]/10 text-[#2e4733]",
  };

  return (
    <div className={`rounded-lg px-3 py-2 text-center ${tones[tone] || tones.neutral}`}>
      <div className="label-eyebrow opacity-70">{label}</div>
      <div className="font-serif-fact text-xl">{value}</div>
    </div>
  );
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadUsers = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getUserStats();
      setUsers(data);
    } catch {
      setError("Kullanıcı istatistikleri yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  return (
    <div className="min-h-screen bg-paper bg-grain">
      <Header />
      <main className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 pt-10 pb-20">
        <div className="flex items-center justify-between gap-3 mb-6">
          <div>
            <Link
              to="/admin"
              className="inline-flex items-center gap-2 text-sm text-[#5c635e] hover:text-[#1c2723] mb-3"
            >
              <ArrowLeft size={14} /> Yönetim paneli
            </Link>
            <h1 className="font-serif-fact text-3xl text-[#1c2723] flex items-center gap-3">
              <Users size={24} className="text-[#2e4733]" />
              Kullanıcılar ve Çözüm İstatistikleri
            </h1>
            <p className="text-sm text-[#5c635e] mt-2">
              Kayıtlı kullanıcıların toplam ve son 24 saatteki çözüm durumları.
            </p>
          </div>
          <Button
            type="button"
            onClick={loadUsers}
            className="rounded-full bg-[#2e4733] hover:bg-[#1c2723]"
          >
            <RefreshCcw size={14} className="mr-2" />
            Yenile
          </Button>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-[#e8eae3] bg-white p-8 text-[#5c635e]">
            Yükleniyor...
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-red-800">
            {error}
          </div>
        ) : users.length === 0 ? (
          <div className="rounded-2xl border border-[#e8eae3] bg-white p-8 text-[#5c635e]">
            Henüz kayıtlı kullanıcı yok.
          </div>
        ) : (
          <div className="space-y-3">
            {users.map((user) => {
              const totalPct = percent(user.total_correct, user.total_answered);
              const recentPct = percent(user.last_24h_correct, user.last_24h_answered);

              return (
                <section
                  key={user.id}
                  className="rounded-2xl border border-[#e8eae3] bg-white px-5 py-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="font-medium text-[#1c2723] text-lg">
                        {user.name || user.username}
                      </div>
                      <div className="label-eyebrow text-[#a3aba6] mt-1">
                        @{user.username}
                      </div>
                      <div className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#f5f6f2] px-3 py-2 text-xs text-[#5c635e]">
                        <Clock3 size={14} />
                        Son giriş: {formatDateTime(user.last_login_at)}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-2">
                      <StatBox label="Toplam soru" value={user.total_answered} />
                      <StatBox label="Toplam doğru" value={user.total_correct} tone="good" />
                      <StatBox label="Toplam yanlış" value={user.total_wrong} tone="bad" />
                      <StatBox label="Toplam başarı" value={`%${totalPct}`} />
                      <StatBox label="24s soru" value={user.last_24h_answered} tone="focus" />
                      <StatBox label="24s başarı" value={`%${recentPct}`} tone="focus" />
                    </div>
                  </div>

                  {user.categories.length > 0 ? (
                    <div className="mt-4 grid md:grid-cols-2 xl:grid-cols-3 gap-2">
                      {user.categories.map((category) => (
                        <div
                          key={category.category_id}
                          className="rounded-lg border border-[#e8eae3] bg-[#fdfdfb] px-3 py-2"
                        >
                          <div className="text-sm font-medium text-[#1c2723]">
                            {category.category_name}
                          </div>
                          <div className="text-xs text-[#5c635e] mt-1">
                            {category.answered} soru · {category.correct} doğru · {category.wrong} yanlış
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-4 text-sm text-[#a3aba6]">
                      Henüz soru çözmemiş.
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
